import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { FlaskConical, CheckCircle2, AlertTriangle } from 'lucide-react';
import { ExperienceProvenance } from '@/components/ExperienceProvenance';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const fmt$ = (v) => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : `$${Math.round(v || 0)}`;

const Overline = ({ children }) => (
  <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{children}</p>
);

export default function BacktestPage() {
  const api = useApi();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/analytics/backtest/latest').then(r => setRun(r.data.run)).catch(() => {}).finally(() => setLoading(false));
  }, [api]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/analytics/backtest/run');
      setRun(data);
      toast.success(`Back-test complete — Gini ${data.gini} across ${data.policies_tested.toLocaleString()} policies`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Back-test failed'); }
    setRunning(false);
  };

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-sm" />)}</div>;

  const strong = run && (run.gini >= 0.2 || (run.frequency_lift && run.frequency_lift >= 1.5));
  const verdictColor = !run ? C.slate : strong ? C.green : run.gini >= 0.05 ? C.amber : C.red;
  const maxFreq = run ? Math.max(...run.bands.map(b => b.claim_frequency), 1) : 1;
  const maxLr = run ? Math.max(...run.bands.map(b => b.loss_ratio), 1) : 1;

  return (
    <div className="space-y-5" data-testid="backtest-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-medium tracking-tight" style={{ color: C.navy }}>Model Loss Back-test</h2>
          <p className="text-sm mt-0.5" style={{ color: C.slate }}>Does the composite risk score predict real claims experience? Score at bind vs paid + reserved losses across the bound book.</p>
        </div>
        <Button onClick={runNow} disabled={running} className="rounded-sm text-white" style={{ background: C.blue }} data-testid="backtest-run-btn">
          <FlaskConical className="w-4 h-4 mr-1.5" />{running ? 'Testing…' : 'Run Back-test'}
        </Button>
      </div>

      {!run ? (
        <Card className="p-10 rounded-sm bg-white text-center" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="backtest-empty">
          <FlaskConical className="w-8 h-8 mx-auto mb-3" style={{ color: C.slate }} />
          <p className="text-sm font-medium" style={{ color: C.navy }}>No back-test on record</p>
          <p className="text-xs mt-1" style={{ color: C.slate }}>Run the first back-test to validate the model against the claims book.</p>
        </Card>
      ) : (
        <>
          <ExperienceProvenance data={run.experience_provenance} testId="backtest-provenance" />
          <Card className="p-5 rounded-sm bg-white flex items-start gap-3" style={{ border: `1px solid ${verdictColor}55`, borderLeft: `4px solid ${verdictColor}` }} data-testid="backtest-verdict">
            {strong ? <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" style={{ color: verdictColor }} /> : <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" style={{ color: verdictColor }} />}
            <div>
              <p className="text-sm font-semibold" style={{ color: C.navy }}>{run.verdict}</p>
              <p className="text-xs mt-1" style={{ color: C.slate }}>{run.basis} · last run {new Date(run.run_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
            </div>
          </Card>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ['Gini / accuracy ratio', run.gini, 'loss concentration explained by score ranking'],
              ['Frequency lift', run.frequency_lift ? `${run.frequency_lift}×` : '—', 'claim frequency: worst bands vs best bands'],
              ['Overall loss ratio', `${run.overall_loss_ratio}%`, 'incurred / written premium, whole book'],
              ['Policies tested', run.policies_tested.toLocaleString(), `${run.policies_with_claims.toLocaleString()} carried claims`],
            ].map(([l, v, d]) => (
              <Card key={l} className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
                <Overline>{l}</Overline>
                <p className="text-3xl font-light tracking-tight mt-1" style={{ color: C.navy, fontVariantNumeric: 'tabular-nums' }}>{v}</p>
                <p className="text-[10px] mt-1" style={{ color: C.slate }}>{d}</p>
              </Card>
            ))}
          </div>

          <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="backtest-bands">
            <Overline>Claims experience by score band</Overline>
            <table className="w-full mt-3 text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>
                  <th className="text-left py-1.5 font-semibold">Band</th>
                  <th className="text-right py-1.5 font-semibold">Policies</th>
                  <th className="text-left py-1.5 pl-6 font-semibold w-[28%]">Claim frequency</th>
                  <th className="text-left py-1.5 pl-6 font-semibold w-[28%]">Loss ratio</th>
                  <th className="text-right py-1.5 font-semibold">Incurred</th>
                </tr>
              </thead>
              <tbody>
                {run.bands.map(b => (
                  <tr key={b.band} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }}>
                    <td className="py-2">
                      <span className="font-medium" style={{ color: C.navy }}>{b.band}</span>
                      <span className="text-[10px] ml-2 font-mono" style={{ color: C.slate }}>{b.range}</span>
                    </td>
                    <td className="py-2 text-right" style={{ color: C.navy, fontVariantNumeric: 'tabular-nums' }}>{b.policies.toLocaleString()}</td>
                    <td className="py-2 pl-6">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full" style={{ background: 'rgba(136,150,168,0.12)' }}>
                          <div className="h-2 rounded-full" style={{ width: `${b.claim_frequency / maxFreq * 100}%`, background: C.teal }} />
                        </div>
                        <span className="text-xs w-12 text-right" style={{ color: C.navy, fontVariantNumeric: 'tabular-nums' }}>{b.claim_frequency}%</span>
                      </div>
                    </td>
                    <td className="py-2 pl-6">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full" style={{ background: 'rgba(136,150,168,0.12)' }}>
                          <div className="h-2 rounded-full" style={{ width: `${Math.min(b.loss_ratio / maxLr * 100, 100)}%`, background: b.loss_ratio > 100 ? C.red : C.amber }} />
                        </div>
                        <span className="text-xs w-12 text-right" style={{ color: C.navy, fontVariantNumeric: 'tabular-nums' }}>{b.loss_ratio}%</span>
                      </div>
                    </td>
                    <td className="py-2 text-right" style={{ color: C.navy, fontVariantNumeric: 'tabular-nums' }}>{fmt$(b.incurred)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="backtest-decisions">
            <Overline>Loss ratio by underwriting decision</Overline>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
              {run.by_decision.filter(d => d.policies >= 5).map(d => (
                <div key={d.decision} className="p-3 rounded-sm" style={{ background: '#F5F7FA' }}>
                  <p className="text-xs font-semibold" style={{ color: C.navy }}>{d.decision}</p>
                  <p className="text-2xl font-light mt-1" style={{ color: d.loss_ratio > 100 ? C.red : C.navy, fontVariantNumeric: 'tabular-nums' }}>{d.loss_ratio}%</p>
                  <p className="text-[10px] mt-0.5" style={{ color: C.slate }}>{d.policies.toLocaleString()} policies · {fmt$(d.incurred)} incurred</p>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
